/**
 * Tenant-role vocabulary shared by the dashboard API and SPA.
 *
 * Two tiers of access control exist. Tier 1 is platform admins
 * (`user.isPlatformAdmin`), a single level that spans every tenant. Tier 2 is
 * this file: per-tenant roles on `member.role`, ranked so each role is a strict
 * superset of the one below it —
 *
 *   viewer  — read-only across the tenant's pages
 *   editor  — content: boxes, catalog enablement, storefront config,
 *             top-up packs, shopper balance credits
 *   admin   — everything: payments, API keys, environments, domains,
 *             tenant settings, team management
 *   owner   — hidden superset of admin; never offered by the UI. Platform
 *             admins impersonating a tenant get this synthetic role, so it
 *             must outrank every gate.
 *
 * Better Auth stores multi-roles comma-separated ("admin,member") and its
 * legacy default role is "member", which historically had editor's powers —
 * both are handled here so every check goes through `canTenantRole`, never a
 * string comparison.
 */

/** Roles the UI offers when inviting or re-assigning a member. */
export const TENANT_ROLES = ["viewer", "editor", "admin"] as const;

export type TenantRole = (typeof TENANT_ROLES)[number];

const RANK: Record<string, number> = {
  viewer: 0,
  member: 1, // legacy alias for editor (pre-RBAC default role)
  editor: 1,
  admin: 2,
  owner: 3,
};

/** True when a (possibly comma-separated) role string meets the minimum. */
export function canTenantRole(role: string, min: TenantRole): boolean {
  const need = RANK[min] ?? Number.POSITIVE_INFINITY;
  return role.split(",").some((r) => (RANK[r.trim()] ?? -1) >= need);
}
